// Prayers & Accountability Page for Iron Forums

import { useEffect, useState, useRef } from 'react';
import { prayersAPI, usersAPI, jarvisAPI, accountabilityGroupsAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { 
  Heart, 
  Plus, 
  X, 
  Circle, 
  Lock, 
  Globe, 
  Calendar,
  CheckCircle2,
  Users,
  List,
  RotateCcw,
  ExternalLink,
  User,
  ChevronDown,
  Sparkles,
  Send,
  Pencil,
  BookOpen,
  Trash2,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import type { PrayerRequest, PrayerJournalEntry, UnifiedPrayer, UserListItem, ChatMessage, AccountabilityGroup } from '../types';
import PrayerNotebook from '../components/PrayerNotebook';
import MicButton from '../components/MicButton';

type TabType = 'all' | 'answered';

export default function Prayers() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [allPrayers, setAllPrayers] = useState<UnifiedPrayer[]>([]);
  const [myPrayers, setMyPrayers] = useState<PrayerRequest[]>([]);
  const [answeredPrayers, setAnsweredPrayers] = useState<PrayerRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal state
  const [showNewPrayerModal, setShowNewPrayerModal] = useState(false);
  const [showAnsweredModal, setShowAnsweredModal] = useState(false);
  const [selectedPrayer, setSelectedPrayer] = useState<PrayerRequest | null>(null);
  
  // Form state
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [visibilityMode, setVisibilityMode] = useState<'private' | 'custom'>('private');
  const [shareWithFacilitator, setShareWithFacilitator] = useState(false);
  const [selectedShareGroupIds, setSelectedShareGroupIds] = useState<number[]>([]);
  const [sharingGroups, setSharingGroups] = useState<AccountabilityGroup[]>([]);
  const [sharingGroupsLoading, setSharingGroupsLoading] = useState(false);
  const [answeredNote, setAnsweredNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Journal / reflection state
  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const [reflectionPrayer, setReflectionPrayer] = useState<PrayerRequest | null>(null);
  const [reflectionText, setReflectionText] = useState('');
  const [expandedJournals, setExpandedJournals] = useState<Set<number>>(new Set());
  const [fullJournalEntries, setFullJournalEntries] = useState<Record<number, PrayerJournalEntry[]>>({});
  
  // "Praying For" fields
  const [prayingForType, setPrayingForType] = useState<'none' | 'member' | 'custom'>('none');
  const [prayingForUserId, setPrayingForUserId] = useState<number | null>(null);
  const [prayingForName, setPrayingForName] = useState('');
  // People the user can tag a prayer to. Sourced from the org-wide member
  // directory (the legacy SnapshotGroup member picker was retired).
  const [groupMembers, setGroupMembers] = useState<UserListItem[]>([]);
  
  // Modal tab state
  const [modalTab, setModalTab] = useState<'jarvis' | 'manual'>('manual');
  
  // Jarvis chat state
  const [jarvisMessages, setJarvisMessages] = useState<ChatMessage[]>([]);
  const [jarvisInput, setJarvisInput] = useState('');
  const [jarvisLoading, setJarvisLoading] = useState(false);
  const jarvisChatEndRef = useRef<HTMLDivElement>(null);
  const jarvisInputRef = useRef<HTMLInputElement>(null);
  
  // Post-creation confirmation state
  const [createdPrayer, setCreatedPrayer] = useState<PrayerRequest | null>(null);
  // Increment to tell PrayerNotebook to refetch (so new prayers appear without page refresh)
  const [notebookRefreshTrigger, setNotebookRefreshTrigger] = useState(0);
  // Edit mode: when set, the new-prayer modal shows the manual form for editing this prayer
  const [editingPrayer, setEditingPrayer] = useState<PrayerRequest | null>(null);
  // Delete confirmation: when set, show "are you sure?" modal before deleting
  const [prayerToDelete, setPrayerToDelete] = useState<PrayerRequest | null>(null);

  // Load prayers based on active tab. The Active tab also loads the user's own
  // prayers so the edit/answer/delete actions can resolve full prayer objects.
  useEffect(() => {
    const loadPrayers = async () => {
      setIsLoading(true);
      try {
        if (activeTab === 'all') {
          const [data, mine] = await Promise.all([
            prayersAPI.getAllActive(),
            prayersAPI.getMyPrayers(),
          ]);
          setAllPrayers(data);
          setMyPrayers(mine.filter(p => !p.is_answered));
        } else if (activeTab === 'answered') {
          const data = await prayersAPI.getAnswered();
          setAnsweredPrayers(data);
        }
      } catch (error) {
        console.error('Error loading prayers:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadPrayers();
  }, [activeTab]);

  // Load people for the "Praying For" dropdown from the org-wide directory.
  const loadGroupMembers = async () => {
    try {
      const directory = await usersAPI.getDirectory();
      setGroupMembers(directory.filter(m => m.id !== user?.id));
    } catch (error) {
      console.error('Error loading members for picker:', error);
    }
  };

  const loadSharingGroups = async () => {
    setSharingGroupsLoading(true);
    try {
      const groups = await accountabilityGroupsAPI.list();
      setSharingGroups(groups);
    } catch (error) {
      console.error('Error loading accountability groups:', error);
      setSharingGroups([]);
    } finally {
      setSharingGroupsLoading(false);
    }
  };

  const toggleShareGroup = (groupId: number) => {
    setSelectedShareGroupIds(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const setPrivateVisibility = () => {
    setVisibilityMode('private');
    setShareWithFacilitator(false);
    setSelectedShareGroupIds([]);
  };

  const openNewPrayerModal = () => {
    loadGroupMembers();
    loadSharingGroups();
    setEditingPrayer(null);
    setPrivateVisibility();
    setModalTab('manual');
    setJarvisMessages([]);
    setJarvisInput('');
    setCreatedPrayer(null);
    setShowNewPrayerModal(true);
  };

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      openNewPrayerModal();
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeNewPrayerModal = () => {
    setShowNewPrayerModal(false);
    setEditingPrayer(null);
    setCreatedPrayer(null);
    setNewTitle('');
    setNewContent('');
    setVisibilityMode('private');
    setShareWithFacilitator(false);
    setSelectedShareGroupIds([]);
    setPrayingForType('none');
    setPrayingForUserId(null);
    setPrayingForName('');
  };

  const openEditPrayerModal = (prayer: PrayerRequest) => {
    loadGroupMembers();
    loadSharingGroups();
    setEditingPrayer(prayer);
    setNewTitle(prayer.title);
    setNewContent(prayer.content);
    setShareWithFacilitator(prayer.share_with_facilitator);
    setSelectedShareGroupIds(prayer.shared_group_ids || []);
    setVisibilityMode(
      prayer.share_with_facilitator || (prayer.shared_group_ids || []).length > 0
        ? 'custom'
        : 'private'
    );
    if (prayer.praying_for_user) {
      setPrayingForType('member');
      setPrayingForUserId(prayer.praying_for_user.id);
      setPrayingForName('');
    } else if (prayer.praying_for_name?.trim()) {
      setPrayingForType('custom');
      setPrayingForUserId(null);
      setPrayingForName(prayer.praying_for_name.trim());
    } else {
      setPrayingForType('none');
      setPrayingForUserId(null);
      setPrayingForName('');
    }
    setCreatedPrayer(null);
    setModalTab('manual');
    setShowNewPrayerModal(true);
  };
  
  // Load Jarvis greeting when modal opens with Jarvis tab
  useEffect(() => {
    if (showNewPrayerModal && modalTab === 'jarvis' && jarvisMessages.length === 0) {
      const fetchGreeting = async () => {
        setJarvisLoading(true);
        try {
          const data = await jarvisAPI.getPrayerCreationGreeting();
          setJarvisMessages([{ role: 'assistant', content: data.greeting }]);
        } catch (error) {
          console.error('Error fetching Jarvis greeting:', error);
          setJarvisMessages([{ role: 'assistant', content: "Hey! What would you like to pray about? I'll help you create a prayer request." }]);
        } finally {
          setJarvisLoading(false);
        }
      };
      fetchGreeting();
    }
  }, [showNewPrayerModal, modalTab, jarvisMessages.length]);
  
  // Auto-scroll Jarvis chat to bottom when new messages arrive
  useEffect(() => {
    jarvisChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [jarvisMessages]);
  
  // Handle sending message to Jarvis
  const handleJarvisSend = async () => {
    if (!jarvisInput.trim() || jarvisLoading) return;
    
    const userMessage = jarvisInput.trim();
    setJarvisInput('');
    setJarvisMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setJarvisLoading(true);
    
    try {
      const conversationHistory = jarvisMessages.map(m => ({
        role: m.role,
        content: m.content
      }));
      const result = await jarvisAPI.createPrayerChat(userMessage, conversationHistory);
      
      setJarvisMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
      
      // Check if Jarvis created a prayer
      if (result.action?.type === 'prayer_created' && result.action.prayer) {
        setCreatedPrayer(result.action.prayer);
        // Add to prayers list
        setMyPrayers(prev => [result.action!.prayer!, ...prev]);
        // Refresh all prayers tab
        if (activeTab === 'all') {
          const data = await prayersAPI.getAllActive();
          setAllPrayers(data);
        }
        setNotebookRefreshTrigger(t => t + 1);
      }
    } catch (error) {
      console.error('Error sending message to Jarvis:', error);
      setJarvisMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I encountered an issue. You can try again or use the 'Create Manually' tab." 
      }]);
    } finally {
      setJarvisLoading(false);
      // Refocus input so user can keep typing
      setTimeout(() => jarvisInputRef.current?.focus(), 100);
    }
  };

  const handleCreatePrayer = async () => {
    if (!newTitle.trim() || !newContent.trim() || isSaving) return;
    
    const prayerData: {
      title: string;
      content: string;
      is_private: boolean;
      share_with_facilitator: boolean;
      shared_group_ids: number[];
      praying_for_user_id?: number;
      praying_for_name?: string;
    } = {
      title: newTitle.trim(),
      content: newContent.trim(),
      is_private: visibilityMode === 'private' ? true : !shareWithFacilitator,
      share_with_facilitator: visibilityMode === 'custom' && shareWithFacilitator,
      shared_group_ids: visibilityMode === 'custom' ? selectedShareGroupIds : [],
    };
    if (prayingForType === 'member' && prayingForUserId) {
      prayerData.praying_for_user_id = prayingForUserId;
    } else if (prayingForType === 'custom' && prayingForName.trim()) {
      prayerData.praying_for_name = prayingForName.trim();
    }

    setIsSaving(true);
    try {
      if (editingPrayer) {
        const updated = await prayersAPI.update(editingPrayer.id, prayerData);
        setMyPrayers(prev => prev.map(p => p.id === updated.id ? updated : p));
        setAnsweredPrayers(prev => prev.map(p => p.id === updated.id ? updated : p));
        setNotebookRefreshTrigger(t => t + 1);
        if (activeTab === 'all') {
          const data = await prayersAPI.getAllActive();
          setAllPrayers(data);
        }
        closeNewPrayerModal();
      } else {
        const prayer = await prayersAPI.create(prayerData);
        setMyPrayers(prev => [prayer, ...prev]);
        setNotebookRefreshTrigger(t => t + 1);
        closeNewPrayerModal();
        if (activeTab === 'all') {
          const data = await prayersAPI.getAllActive();
          setAllPrayers(data);
        } else {
          setActiveTab('all');
        }
      }
    } catch (error) {
      console.error(editingPrayer ? 'Error updating prayer:' : 'Error creating prayer:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkAnswered = async () => {
    if (!selectedPrayer || isSaving) return;
    
    setIsSaving(true);
    try {
      const updated = await prayersAPI.markAnswered(selectedPrayer.id, answeredNote);
      // Remove from my prayers list
      setMyPrayers(prev => prev.filter(p => p.id !== selectedPrayer.id));
      // Add to answered list
      setAnsweredPrayers(prev => [updated, ...prev]);
      setShowAnsweredModal(false);
      setSelectedPrayer(null);
      setAnsweredNote('');
    } catch (error) {
      console.error('Error marking prayer as answered:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkUnanswered = async (prayer: PrayerRequest) => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const updated = await prayersAPI.markUnanswered(prayer.id);
      // Remove from answered list
      setAnsweredPrayers(prev => prev.filter(p => p.id !== prayer.id));
      // Add back to my prayers list
      setMyPrayers(prev => [updated, ...prev]);
    } catch (error) {
      console.error('Error marking prayer as unanswered:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Journal / Reflection handlers ----

  const openReflectionModal = (prayer: PrayerRequest) => {
    setReflectionPrayer(prayer);
    setReflectionText('');
    setShowReflectionModal(true);
  };

  const handleAddReflection = async () => {
    if (!reflectionPrayer || !reflectionText.trim() || isSaving) return;
    setIsSaving(true);
    try {
      await prayersAPI.addJournalEntry(reflectionPrayer.id, reflectionText.trim());
      // Refresh the prayer data to pick up the new entry
      const refreshed = await prayersAPI.getAll();
      setMyPrayers(refreshed.filter((p: PrayerRequest) => p.user.id === user?.id && !p.is_answered));
      setAnsweredPrayers(refreshed.filter((p: PrayerRequest) => p.user.id === user?.id && p.is_answered));
      setShowReflectionModal(false);
      setReflectionPrayer(null);
      setReflectionText('');
    } catch (error) {
      console.error('Error adding reflection:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleJournalExpanded = async (prayerId: number) => {
    const next = new Set(expandedJournals);
    if (next.has(prayerId)) {
      next.delete(prayerId);
    } else {
      next.add(prayerId);
      if (!fullJournalEntries[prayerId]) {
        try {
          const entries = await prayersAPI.getJournalEntries(prayerId);
          setFullJournalEntries(prev => ({ ...prev, [prayerId]: entries }));
        } catch (error) {
          console.error('Error loading journal entries:', error);
        }
      }
    }
    setExpandedJournals(next);
  };

  const handleDeleteJournalEntry = async (prayerId: number, entryId: number) => {
    try {
      await prayersAPI.deleteJournalEntry(prayerId, entryId);
      setFullJournalEntries(prev => ({
        ...prev,
        [prayerId]: (prev[prayerId] || []).filter(e => e.id !== entryId),
      }));
      // Also refresh answered prayers to update inline entries
      const refreshed = await prayersAPI.getAll();
      setAnsweredPrayers(refreshed.filter((p: PrayerRequest) => p.user.id === user?.id && p.is_answered));
    } catch (error) {
      console.error('Error deleting journal entry:', error);
    }
  };

  const openDeleteConfirmModal = (prayerOrId: PrayerRequest | number) => {
    const id = typeof prayerOrId === 'number' ? prayerOrId : prayerOrId.id;
    const prayer = myPrayers.find(p => p.id === id) ?? answeredPrayers.find(p => p.id === id);
    if (prayer) setPrayerToDelete(prayer);
  };

  const confirmDeletePrayer = async () => {
    if (!prayerToDelete) return;
    const id = prayerToDelete.id;
    try {
      await prayersAPI.delete(id);
      setMyPrayers(prev => prev.filter(p => p.id !== id));
      setAnsweredPrayers(prev => prev.filter(p => p.id !== id));
      if (activeTab === 'all') {
        const data = await prayersAPI.getAllActive();
        setAllPrayers(data);
      }
      setNotebookRefreshTrigger(t => t + 1);
    } catch (error) {
      console.error('Error deleting prayer:', error);
    } finally {
      setPrayerToDelete(null);
    }
  };

  const openAnsweredModal = (prayer: PrayerRequest) => {
    setSelectedPrayer(prayer);
    setAnsweredNote('');
    setShowAnsweredModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'all', label: 'Active', icon: <List size={18} /> },
    { id: 'answered', label: 'Answered', icon: <CheckCircle2 size={18} /> },
  ];

  if (isLoading && activeTab === 'all' && allPrayers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">
            Prayers & Accountability
          </h1>
          <p className="text-gray-600 mt-1">Track your prayer requests and celebrate answered prayers</p>
        </div>
        <button
          onClick={openNewPrayerModal}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-xl hover:bg-navy-dark transition"
        >
          <Plus size={20} />
          <span>New Prayer</span>
        </button>
      </div>

      {/* Prayer Notebook - Person-based prayer organization */}
      <PrayerNotebook refreshTrigger={notebookRefreshTrigger} />

      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-px overflow-x-auto">
        <div className="flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-navy border-b-2 border-navy -mb-px'
                  : 'text-gray-500 hover:text-navy'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
        <Link
          to="/accountability-groups"
          className="flex items-center gap-1.5 pb-2 text-sm text-navy hover:text-navy-dark font-medium whitespace-nowrap"
        >
          <Users size={15} />
          Accountability Groups
          <ExternalLink size={13} />
        </Link>
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
          </div>
        ) : activeTab === 'all' ? (
          // All Prayers Unified View
          allPrayers.length > 0 ? (
            <div className="space-y-4">
              {allPrayers.map((prayer) => (
                <div
                  key={`${prayer.type}-${prayer.id}`}
                  className="p-4 border border-gray-100 rounded-xl hover:border-gold transition"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {/* Person indicator */}
                        <span className="text-sm font-medium text-navy">
                          {prayer.is_own ? 'You' : `${prayer.user.first_name} ${prayer.user.last_name}`}
                        </span>
                        <span className="text-gray-400">•</span>
                        
                        {/* Date */}
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar size={14} />
                          {prayer.snapshot_month 
                            ? formatDate(prayer.snapshot_month)
                            : formatDate(prayer.created_at)
                          }
                        </span>
                        
                        {/* Source badge */}
                        {prayer.source === 'personal' && prayer.is_private && (prayer.shared_group_ids?.length || 0) === 0 && (
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Lock size={12} />
                            Private
                          </span>
                        )}
                        {prayer.source === 'personal' && (prayer.shared_group_ids?.length || 0) > 0 && (
                          <span className="flex items-center gap-1 text-xs text-navy">
                            <Users size={12} />
                            Group shared
                          </span>
                        )}
                        {prayer.source === 'personal' && prayer.share_with_facilitator && (
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Globe size={12} />
                            Facilitator
                          </span>
                        )}
                        {prayer.source === 'group' && (
                          <span className="flex items-center gap-1 text-xs text-navy">
                            <Users size={12} />
                            Group
                          </span>
                        )}
                        {prayer.source === 'snapshot' && (
                          <span className="px-2 py-0.5 bg-off-white text-navy text-xs rounded-lg">
                            From Snapshot
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-800">{prayer.title}</h3>
                      <p className="text-gray-600 mt-1">{prayer.content}</p>
                    </div>
                    
                    {/* Actions - only for user's own regular prayers */}
                    {prayer.is_own && prayer.type === 'prayer' && typeof prayer.id === 'number' && (() => {
                      const fullPrayer = myPrayers.find(p => p.id === prayer.id) ?? answeredPrayers.find(p => p.id === prayer.id);
                      return fullPrayer ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openAnsweredModal(fullPrayer)}
                            className="p-2 border border-gray-300 text-gray-500 hover:border-green-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                            title="Mark as Answered"
                            aria-label="Mark as Answered"
                          >
                            <Circle size={20} />
                          </button>
                          <button
                            onClick={() => openEditPrayerModal(fullPrayer)}
                            className="p-2 text-gray-400 hover:text-navy hover:bg-navy/5 rounded-lg transition"
                            title="Edit"
                            aria-label="Edit"
                          >
                            <Pencil size={20} />
                          </button>
                          <button
                            onClick={() => openDeleteConfirmModal(fullPrayer)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                            title="Delete"
                            aria-label="Delete"
                          >
                            <X size={20} />
                          </button>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Heart className="mx-auto text-gray-300" size={48} />
              <p className="text-gray-500 mt-4">No active prayer requests yet</p>
              <p className="text-sm text-gray-400 mt-1">Add a prayer request or share one with your group</p>
              <button
                onClick={openNewPrayerModal}
                className="mt-4 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition"
              >
                Add Prayer Request
              </button>
            </div>
          )
        ) : answeredPrayers.length > 0 ? (
          // Answered Prayers View
          <div className="space-y-4">
            {answeredPrayers.map((prayer) => (
              <div
                key={prayer.id}
                className={`p-4 border rounded-xl transition ${
                  prayer.is_answered 
                    ? 'border-green-200 bg-green-50/30' 
                    : 'border-gray-100 hover:border-gold'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-navy">
                        {prayer.user.id === user?.id ? 'You' : `${prayer.user.first_name} ${prayer.user.last_name}`}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(prayer.created_at)}
                      </span>
                      {prayer.is_private && prayer.shared_group_ids.length === 0 ? (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Lock size={12} />
                          Private
                        </span>
                      ) : null}
                      {prayer.shared_group_ids.length > 0 && (
                        <span className="flex items-center gap-1 text-xs text-navy">
                          <Users size={12} />
                          Group shared
                        </span>
                      )}
                      {prayer.share_with_facilitator && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Globe size={12} />
                          Facilitator
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-800">{prayer.title}</h3>
                    <p className="text-gray-600 mt-1">{prayer.content}</p>
                    
                    {prayer.is_answered && (
                      <div className="mt-3">
                        <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                          <p className="text-sm font-medium text-green-800 flex items-center gap-1">
                            <CheckCircle2 size={16} />
                            Answered on {prayer.answered_at && formatDate(prayer.answered_at)}
                          </p>
                        </div>

                        {/* Journal entries */}
                        {prayer.journal_entries && prayer.journal_entries.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {(expandedJournals.has(prayer.id)
                              ? (fullJournalEntries[prayer.id] || prayer.journal_entries)
                              : prayer.journal_entries.slice(0, 2)
                            ).map((entry) => (
                              <div key={entry.id} className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-amber-700 font-medium flex items-center gap-1">
                                    <BookOpen size={12} />
                                    {formatDate(entry.created_at)}
                                    {entry.is_private && <Lock size={10} />}
                                  </span>
                                  {entry.user.id === user?.id && (
                                    <button
                                      onClick={() => handleDeleteJournalEntry(prayer.id, entry.id)}
                                      className="text-gray-400 hover:text-red-500 transition"
                                      aria-label="Delete"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  )}
                                </div>
                                <p className="text-sm text-gray-700">{entry.content}</p>
                              </div>
                            ))}
                            {prayer.journal_count > 2 && (
                              <button
                                onClick={() => toggleJournalExpanded(prayer.id)}
                                className="text-xs text-navy hover:underline"
                              >
                                {expandedJournals.has(prayer.id)
                                  ? 'Show less'
                                  : `Show all ${prayer.journal_count} reflections`}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Add Reflection button */}
                        {prayer.user.id === user?.id && (
                          <button
                            onClick={() => openReflectionModal(prayer)}
                            className="mt-2 text-sm text-navy hover:text-[#032a5a] flex items-center gap-1 transition"
                          >
                            <BookOpen size={14} />
                            Add Reflection
                          </button>
                        )}
                      </div>
                    )}

                  </div>

                  {/* Actions - only for user's own prayers */}
                  {prayer.user.id === user?.id && (
                    <div className="flex items-center gap-2">
                      {!prayer.is_answered ? (
                        <button
                          onClick={() => openAnsweredModal(prayer)}
                          className="p-2 border border-gray-300 text-gray-500 hover:border-green-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                          title="Mark as Answered"
                          aria-label="Mark as Answered"
                        >
                          <Circle size={20} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMarkUnanswered(prayer)}
                          disabled={isSaving}
                          className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg transition disabled:opacity-50"
                          title="Mark as Unanswered"
                          aria-label="Mark as Unanswered"
                        >
                          <RotateCcw size={20} />
                        </button>
                      )}
                      <button
                        onClick={() => openEditPrayerModal(prayer)}
                        className="p-2 text-gray-400 hover:text-navy hover:bg-navy/5 rounded-lg transition"
                        title="Edit"
                        aria-label="Edit"
                      >
                        <Pencil size={20} />
                      </button>
                      <button
                        onClick={() => openDeleteConfirmModal(prayer)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                        title="Delete"
                        aria-label="Delete"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Empty State (Answered tab)
          <div className="text-center py-12">
            <CheckCircle2 className="mx-auto text-gray-300" size={48} />
            <p className="text-gray-500 mt-4">No answered prayers yet</p>
            <p className="text-sm text-gray-400 mt-1">When you mark prayers as answered, they'll appear here</p>
          </div>
        )}
      </div>

      {/* New Prayer Modal */}
      {showNewPrayerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] transition-opacity duration-200" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[min(90vh,calc(100dvh-1.5rem))] flex flex-col transition-all duration-200">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-navy">
                {editingPrayer ? 'Edit Prayer Request' : 'New Prayer Request'}
              </h2>
              <button
                onClick={closeNewPrayerModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Post-creation confirmation */}
              {createdPrayer ? (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="text-green-600" size={32} />
                  </div>
                  <h3 className="text-lg font-semibold text-navy mb-2">Prayer Request Added!</h3>
                  <p className="text-gray-600 mb-4">
                    Your prayer "{createdPrayer.title}" has been saved {createdPrayer.is_private ? 'privately' : 'and is now shareable with your accountability groups'}.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setCreatedPrayer(null);
                        setJarvisMessages([]);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                    >
                      Add Another
                    </button>
                    <button
                      onClick={closeNewPrayerModal}
                      className="flex-1 px-4 py-2 bg-navy text-white rounded-xl hover:bg-navy-dark transition"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Tabs */}
                  <div className="flex border-b border-gray-100">
                    <button
                      type="button"
                      onClick={() => setModalTab('manual')}
                      className={`flex-1 py-3 text-sm font-medium border-b-2 transition ${
                        modalTab === 'manual'
                          ? 'border-navy text-navy'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Create Manually
                    </button>
                    <button
                      type="button"
                      onClick={() => setModalTab('jarvis')}
                      className={`flex-1 py-3 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                        modalTab === 'jarvis'
                          ? 'border-navy text-navy'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Sparkles size={16} />
                      Ask IF
                    </button>
                  </div>
                  
                  {/* Manual Form */}
                  {modalTab === 'manual' && (
                    <div className="p-4 space-y-4 overflow-y-auto">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          placeholder="Brief title for your prayer request"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prayer Request
                        </label>
                        <div className="relative">
                          <textarea
                            value={newContent}
                            onChange={(e) => setNewContent(e.target.value)}
                            placeholder="Share what you'd like prayer for..."
                            rows={4}
                            className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none resize-none"
                          />
                          <MicButton
                            value={newContent}
                            onChange={setNewContent}
                            className="absolute bottom-2 right-2"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Visibility
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={setPrivateVisibility}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border transition ${
                              visibilityMode === 'private'
                                ? 'border-navy bg-navy/5 text-navy'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            <Lock size={18} />
                            <span>Private</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setVisibilityMode('custom')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border transition ${
                              visibilityMode === 'custom'
                                ? 'border-navy bg-navy/5 text-navy'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            <Users size={18} />
                            <span>Share</span>
                          </button>
                        </div>
                        {visibilityMode === 'private' ? (
                          <p className="text-xs text-gray-500 mt-2">
                            Only you will see this prayer request.
                          </p>
                        ) : (
                          <div className="mt-3 space-y-3 rounded-xl border border-gray-100 bg-off-white/40 p-3">
                            <label className="flex items-start gap-3 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={shareWithFacilitator}
                                onChange={(e) => setShareWithFacilitator(e.target.checked)}
                                className="mt-1 rounded border-gray-300 text-navy focus:ring-navy"
                              />
                              <span>
                                <span className="font-medium text-navy">Share with my facilitator</span>
                                <span className="block text-xs text-gray-500">
                                  Facilitators for your forum can read and pray over this request.
                                </span>
                              </span>
                            </label>

                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">
                                Share with accountability groups
                              </p>
                              {sharingGroupsLoading ? (
                                <p className="text-xs text-gray-500">Loading your groups...</p>
                              ) : sharingGroups.length === 0 ? (
                                <p className="text-xs text-gray-500">
                                  Join an accountability group to share prayers with a group.
                                </p>
                              ) : (
                                <div className="space-y-2">
                                  {sharingGroups.map((group) => (
                                    <label key={group.id} className="flex items-center gap-3 text-sm text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={selectedShareGroupIds.includes(group.id)}
                                        onChange={() => toggleShareGroup(group.id)}
                                        className="rounded border-gray-300 text-navy focus:ring-navy"
                                      />
                                      <span>{group.name}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>

                            {!shareWithFacilitator && selectedShareGroupIds.length === 0 && (
                              <p className="text-xs text-amber-700">
                                Select at least one audience, or switch back to Private.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Praying For - Optional */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Praying For <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <div className="space-y-3">
                          {/* Selection buttons */}
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setPrayingForType('none');
                                setPrayingForUserId(null);
                                setPrayingForName('');
                              }}
                              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition ${
                                prayingForType === 'none'
                                  ? 'border-navy bg-navy/5 text-navy'
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <span>Not specified</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPrayingForType('member');
                                setPrayingForName('');
                              }}
                              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition ${
                                prayingForType === 'member'
                                  ? 'border-navy bg-navy/5 text-navy'
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <Users size={16} />
                              <span>Member</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPrayingForType('custom');
                                setPrayingForUserId(null);
                              }}
                              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition ${
                                prayingForType === 'custom'
                                  ? 'border-navy bg-navy/5 text-navy'
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <User size={16} />
                              <span>Other</span>
                            </button>
                          </div>

                          {/* Group member dropdown */}
                          {prayingForType === 'member' && (
                            <div className="relative">
                              <select
                                value={prayingForUserId || ''}
                                onChange={(e) => setPrayingForUserId(e.target.value ? Number(e.target.value) : null)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none appearance-none bg-white"
                              >
                                <option value="">Select a member...</option>
                                {groupMembers.map((member) => (
                                  <option key={member.id} value={member.id}>
                                    {member.full_name}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                          )}

                          {/* Custom name input */}
                          {prayingForType === 'custom' && (
                            <input
                              type="text"
                              value={prayingForName}
                              onChange={(e) => setPrayingForName(e.target.value)}
                              placeholder="e.g., Mom, Pastor John, My neighbor..."
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                            />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Organize your prayers like a prayer notebook - by who you're praying for
                        </p>
                      </div>
                      
                      {/* Manual form buttons */}
                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={closeNewPrayerModal}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreatePrayer}
                          disabled={
                            !newTitle.trim()
                            || !newContent.trim()
                            || isSaving
                            || (visibilityMode === 'custom'
                              && !shareWithFacilitator
                              && selectedShareGroupIds.length === 0)
                          }
                          className="flex-1 px-4 py-2 bg-navy text-white rounded-xl hover:bg-navy-dark transition disabled:opacity-50"
                        >
                          {isSaving ? 'Saving...' : editingPrayer ? 'Update Prayer' : 'Save Prayer'}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Jarvis Chat */}
                  {modalTab === 'jarvis' && (
                    <div className="flex flex-col h-[400px] p-4">
                      {/* Chat Messages */}
                      <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                        {jarvisMessages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            {msg.role === 'assistant' && (
                              <div className="w-8 h-8 bg-gradient-to-br from-navy to-gold rounded-full flex items-center justify-center flex-shrink-0">
                                <Sparkles className="text-white" size={14} />
                              </div>
                            )}
                            <div
                              className={`max-w-[80%] p-3 rounded-2xl whitespace-pre-wrap ${
                                msg.role === 'user'
                                  ? 'bg-navy text-white rounded-br-md'
                                  : 'bg-off-white text-gray-800 rounded-bl-md'
                              }`}
                            >
                              {msg.content}
                            </div>
                          </div>
                        ))}
                        
                        {jarvisLoading && (
                          <div className="flex gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-navy to-gold rounded-full flex items-center justify-center flex-shrink-0">
                              <Sparkles className="text-white" size={14} />
                            </div>
                            <div className="bg-off-white p-3 rounded-2xl rounded-bl-md">
                              <div className="flex gap-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                              </div>
                            </div>
                          </div>
                        )}
                        <div ref={jarvisChatEndRef} />
                      </div>
                      
                      {/* Input */}
                      <div className="border-t border-gray-100 pt-4">
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <input
                              ref={jarvisInputRef}
                              type="text"
                              value={jarvisInput}
                              onChange={(e) => setJarvisInput(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleJarvisSend()}
                              placeholder="Share what's on your heart..."
                              className="w-full px-4 py-2 pr-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-sm"
                              disabled={jarvisLoading}
                            />
                            <MicButton
                              value={jarvisInput}
                              onChange={setJarvisInput}
                              className="absolute top-1/2 -translate-y-1/2 right-1.5"
                            />
                          </div>
                          <button
                            onClick={handleJarvisSend}
                            disabled={!jarvisInput.trim() || jarvisLoading}
                            className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50"
                            aria-label="Send message"
                          >
                            <Send size={16} />
                          </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-2 text-center">
                          Or <button type="button" onClick={() => setModalTab('manual')} className="text-navy hover:underline">create manually</button>
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {prayerToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 transition-opacity duration-200" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 transition-all duration-200">
            <h2 className="text-lg font-bold text-navy mb-3">
              Delete this prayer?
            </h2>
            <p className="text-gray-600 text-sm mb-2">
              Was this prayer answered? Consider marking it as answered instead so you can celebrate what God has done.
            </p>
            <p className="text-gray-600 text-sm mb-2">
              Created by mistake? You can remove it below.
            </p>
            <p className="text-gray-600 text-sm mb-4">
              Otherwise, we encourage you to keep praying—God hears you.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  const prayer = prayerToDelete;
                  setPrayerToDelete(null);
                  openAnsweredModal(prayer);
                }}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition"
              >
                Mark as Answered
              </button>
              <button
                onClick={() => setPrayerToDelete(null)}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePrayer}
                className="w-full px-4 py-2 border border-red-300 text-red-600 rounded-xl hover:bg-red-50 transition"
              >
                Delete prayer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Answered Modal */}
      {showAnsweredModal && selectedPrayer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 transition-opacity duration-200" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 transition-all duration-200">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-navy">
                Mark Prayer as Answered
              </h2>
              <button
                onClick={() => setShowAnsweredModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="p-3 bg-off-white rounded-lg">
                <p className="font-medium text-navy">{selectedPrayer.title}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedPrayer.content}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  How was this prayer answered? (optional)
                </label>
                <div className="relative">
                  <textarea
                    value={answeredNote}
                    onChange={(e) => setAnsweredNote(e.target.value)}
                    placeholder="Share how God answered this prayer..."
                    rows={3}
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none resize-none"
                  />
                  <MicButton
                    value={answeredNote}
                    onChange={setAnsweredNote}
                    className="absolute bottom-2 right-2"
                  />
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowAnsweredModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAnswered}
                disabled={isSaving}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <CheckCircle2 size={18} />
                {isSaving ? 'Saving...' : 'Mark as Answered'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Reflection Modal */}
      {showReflectionModal && reflectionPrayer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 transition-opacity duration-200" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 transition-all duration-200">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-navy">
                Add Reflection
              </h2>
              <button
                onClick={() => setShowReflectionModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                <p className="font-medium text-green-800 flex items-center gap-1">
                  <CheckCircle2 size={16} />
                  {reflectionPrayer.title}
                </p>
                <p className="text-sm text-green-700 mt-1">{reflectionPrayer.content}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your reflection
                </label>
                <div className="relative">
                  <textarea
                    value={reflectionText}
                    onChange={(e) => setReflectionText(e.target.value)}
                    placeholder="How did God answer this prayer? What did you learn? Is there a scripture that speaks to this?"
                    rows={4}
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent outline-none resize-none"
                  />
                  <MicButton
                    value={reflectionText}
                    onChange={setReflectionText}
                    className="absolute bottom-2 right-2"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowReflectionModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAddReflection}
                disabled={isSaving || !reflectionText.trim()}
                className="flex-1 px-4 py-2 bg-navy text-white rounded-xl hover:bg-[#032a5a] transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <BookOpen size={18} />
                {isSaving ? 'Saving...' : 'Save Reflection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
